import Link from 'next/link';
import { MouseEvent, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { IS_PREBOOK_FLOW } from '@/constants/common';
import { CartContext } from '@/context/CartProvider';
import useIsMobile from '@/hooks/useIsMobile';
import { ComboDiscountType, ComboType } from '@/models/Combo';
import styles from '@/styles/TruncateContent.module.css';
import { getLocalStorageKey } from '@/utils/helper';
import BottomDrawer from '../BottomDrawer';
import Paragraph, { VARIANT as PARAGRAPH_VARIANT } from '../Paragraph';
import ComboAddToCart from '../products/combo/ComboAddToCart';
import ComboVariantList from '../products/combo/ComboVariantList';
import ProductListingImage from '../products/ProductListingImage';
import ProductPrice from '../products/ProductPrice';
import VariantLabel from '../products/VariantLabel';
interface Props {
  combo: ComboType;
  position?: number;
  componentName?: string;
  componentId?: string | number;
}
const ComboCard = ({
  combo,
  position,
  componentName,
  componentId
}: Props) => {
  const {
    comboItems
  } = useContext(CartContext);
  const {
    isMobile
  } = useIsMobile();
  const bottomDrawerRef = useRef<HTMLDivElement>(null);
  const [showVariantDrawer, setShowVariantDrawer] = useState(false);
  const isParentOutOfStock = useMemo(() => combo.variants?.every(v => !v.is_sellable), [combo]);
  const sellableVariants = useMemo(() => combo.variants?.filter(v => v.is_sellable) || [], [combo]);
  const activeVariants = sellableVariants;
  const activeVariant = sellableVariants[0];
  const isMultipleVariantsInCart = useMemo(() => {
    if (sellableVariants.length > 1) {
      const variantsInCart = combo.variants.filter(variant => comboItems[variant.id]);
      return variantsInCart.length >= 1;
    }
    return false;
  }, [sellableVariants.length, combo.variants, comboItems]);
  const isMultipleVariants = useMemo(() => {
    return sellableVariants.length > 0 || isMultipleVariantsInCart;
  }, [sellableVariants.length, isMultipleVariantsInCart]);
  const actualPrice = activeVariant?.price || 0;
  const discountedPrice = actualPrice - (activeVariant?.discount_amount || 0);
  const slug = combo.slug_info?.slug;
  const stopPropagation = useCallback((e: MouseEvent<HTMLElement>) => {
    if (isMultipleVariants) {
      e.stopPropagation();
      e.preventDefault();
    }
  }, [isMultipleVariants]);
  const toggleBottomDrawer = useCallback(() => {
    if (!isParentOutOfStock && isMultipleVariants) setShowVariantDrawer(prev => !prev);
  }, [isMultipleVariants, isParentOutOfStock]);
  const quantity = useMemo(() => {
    if (isMultipleVariants) {
      return combo.variants.reduce((sum, v) => sum + (comboItems[v.id] || 0), 0);
    }
    return comboItems[activeVariant?.id] || 0;
  }, [comboItems, combo.variants, activeVariant, isMultipleVariants]);
  const variantAcres = activeVariant?.applicable_acres || combo.variants[0]?.applicable_acres;
  const variantLabel = variantAcres ? `${variantAcres} acre` : '';
  const isPrebook = !!getLocalStorageKey(IS_PREBOOK_FLOW);
  return <>
      <Link href={`/product/combo/${slug}${isPrebook ? '?isprebook=true' : ''}`} className="block w-full max-w-[240px]" data-sentry-element="Link" data-sentry-source-file="ComboCard.tsx">
        <section className={`pb-2 flex flex-col h-[22.5rem] lg:h-[23rem] bg-white px-2 rounded-xl overflow-hidden relative flex-1 hover:shadow-xl transition-shadow duration-300  shadow-[0_4px_14px_-6px_rgba(24,39,75,0.12),_0_0_32px_-8px_rgba(24,39,75,0.10)] ease-in-out w-full`}>
          <div className="relative flex flex-col items-center">
            <div className="relative flex justify-center w-full text-center">
              <ProductListingImage imageUrl={combo.image} productName={combo.name} imageClassName={'w-32 h-36'} outOfStockImageClassName={'w-32 h-36'} fallbackClassName="w-32 h-36" position={position} isOutOfStock={isParentOutOfStock} data-sentry-element="ProductListingImage" data-sentry-source-file="ComboCard.tsx" />
            </div>
          </div>

          <>
            <div className="h-[32px] lg:h-[40px]">
              <Paragraph variant={isMobile ? PARAGRAPH_VARIANT.SMALL : PARAGRAPH_VARIANT.MEDIUM} isHighlighted className={`text-neutral-90 ${styles.truncate_after_two_lines}`} data-sentry-element="Paragraph" data-sentry-source-file="ComboCard.tsx">
                {combo.name}
              </Paragraph>
            </div>
            <div className="h-[32px]">
              {!isMultipleVariantsInCart && combo.description ? <Paragraph className={`text-xs font-semibold text-neutral-text-light-70 ${styles.truncate_after_two_lines}`}>
                  {combo.description}
                </Paragraph> : null}
            </div>

            <div className="flex flex-row items-center gap-1 mt-1 h-[24px]">
              {/* No ratings for combos, but keep height for alignment */}
            </div>
          </>
          <div className="gap-2 bg-white rounded-b-xl w-full" onClick={stopPropagation}>
            {!isMultipleVariantsInCart && <div className={`flex items-center mb-1 h-[24px]`}>
                {isParentOutOfStock ? null : <ProductPrice actualPrice={actualPrice} discountedPrice={discountedPrice} showDiscountPercentage={activeVariant.discount_type === ComboDiscountType.PERCENTAGE} />}
              </div>}
            {!isMultipleVariantsInCart && <div className="h-[36px]">
                <VariantLabel variantLabel={variantLabel} variantLength={activeVariants.length} onClick={toggleBottomDrawer} isOutOfStock={isParentOutOfStock} />
              </div>}

            {isMultipleVariantsInCart && quantity > 0 && <div className="flex flex-col gap-y-2 h-32 overflow-y-scroll">
                {activeVariants.map(variant => comboItems[variant.id] ? <button key={variant.id} className="flex justify-between items-center gap-y-2 px-2 py-1 border border-gray-300 rounded-md" onClick={toggleBottomDrawer}>
                      <Paragraph variant={PARAGRAPH_VARIANT.MEDIUM} className="font-medium text-[#33393D] grow-0 shrink-0">
                        {variant.applicable_acres} acre
                      </Paragraph>
                      <p className="bg-primary-100 px-2 py-0.5 rounded-xl text-white text-sm">
                        {comboItems[variant.id]}
                      </p>
                    </button> : null)}
              </div>}
            <ComboAddToCart productName={`${combo?.type} - ${combo?.name} - ${sellableVariants[0]?.applicable_acres}`} productVariant={sellableVariants[0]} index={position} isOutOfStock={isParentOutOfStock} length={sellableVariants.length} onClick={toggleBottomDrawer} quantity={quantity} product={combo} data-sentry-element="ComboAddToCart" data-sentry-source-file="ComboCard.tsx" />
          </div>
        </section>
      </Link>

      {isMultipleVariants && <BottomDrawer show={showVariantDrawer} onClose={() => setShowVariantDrawer(false)} ref={bottomDrawerRef} drawerStyle="md:max-w-xl">
          <section className="bg-gray-10 shadow px-4 pt-4 pb-6 rounded-t-xl">
            <ComboVariantList productVariants={activeVariants} onContine={() => setShowVariantDrawer(false)} componentName={componentName} componentId={componentId} product={combo} />
          </section>
        </BottomDrawer>}
    </>;
};
export default ComboCard;